import React from 'react';
import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useHistory} from 'react-router-dom';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {Constants, HistoryRoutes, Messages, SnackbarVariants, ViewKeys} from 'commons/constants';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {EfeAccordionValues} from 'commons';
import {
    DigitalizedSignatureValues,
    Documents,
    EfeResponse,
    Field,
    UploadDocumentServiceRequest,
} from 'commons/models';
import {useFlowFieldSearch, useSpinner} from 'hooks';
import ConsigneeSignForm from './consignee-sign-form/ConsigneeSignForm';
import {MainContent} from 'layouts/commons';
import {EfeAccordion} from 'components';
import {getBase64Extension, nextStep} from 'commons/utils/Functions';
import {ClientService, DocumentServices} from 'services';
import {ConsigneeData} from 'commons/types/Types';

function ConsigneeSign() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const {menu, kycId, expedientId, fatherKycId} = useMenuContext();
    const [isLoading, setIsLoading] = React.useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const [signed, setSigned] = React.useState<boolean>(false);
    const [sign, setSign] = React.useState<any>(undefined);
    const [allowSign, setAllowSign] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (fatherKycId > 0 && kycId && ViewKeys.V_CONSIGNEE_SIGN === flow.data.expandKey) {
            setIsLoading(true);
            (async function () {
                const response: EfeResponse<ConsigneeData> = await ClientService.getConsigneeData(
                    kycId
                );
                if (response && response.data && response.data.swAccountData) {
                    setAllowSign(true);
                    try {
                        const response: EfeResponse<Documents> =
                            await DocumentServices.getDocumentByKycId(
                                Constants.DOC_FIRMA_DIGITALIZADA_CT,
                                expedientId,
                                kycId
                            );
                        if (response && response.data) {
                            setSign(response.data.data);
                            setSigned(true);
                        }
                        setIsLoading(false);
                    } catch (error) {
                        setIsLoading(false);
                    }
                } else {
                    setIsLoading(false);
                }
            })();
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Firma Digitalizada',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <ConsigneeSignForm
                            fields={fields}
                            submitHandler={onSubmit}
                            signed={signed}
                            signature={sign}
                            allowSign={allowSign}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.V_CONSIGNEE_SIGN === flow.data.expandKey,
        },
    ];

    async function onSubmit(values: DigitalizedSignatureValues) {
        if (!values || !values.signature) {
            setIsLoading(true);
            const path = nextStep(flow, menu);
            updateModuleStep(true);
            flow.data.expandKey = path;
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const fileConverted = await (await fetch(values.signature)).blob();
            const extension = getBase64Extension(values.signature);
            const request: UploadDocumentServiceRequest = {
                documentKey: Constants.DOC_FIRMA_DIGITALIZADA_CT,
                expedientId: expedientId,
                file: fileConverted,
                kycId: kycId,
                filename: `${Constants.DOC_FIRMA_DIGITALIZADA_CT}.${extension}`,
            };

            const response: EfeResponse<any> = await DocumentServices.updateDocument(request);
            if (response && response.data) {
                enqueueSnackbar(Messages.LOADING_FILE, {
                    variant: SnackbarVariants.SUCCESS,
                });
                const path = nextStep(flow, menu);
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                variant: SnackbarVariants.ERROR,
            });
            history.push('/admin/consignee-info');
        }
    }

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.CONSIGNEE_SIGN)}
                values={accordion}
            />
        </MainContent>
    );
}

export default ConsigneeSign;
